import {
  Button,
  Form,
  IconNotification,
  Input,
  LoadingIndicator,
} from "@light-technology/components";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { createSearchParams, useNavigate } from "react-router";
import { toast } from "sonner";
import { api } from "../../state/api";
import { extractFieldErrors } from "../../utilities/errors";
import { PasswordStrength } from "../shared/PasswordStrength";

type AcceptInviteCreateProps = {
  token: string;
};

type AcceptInviteCreateForm = {
  first_name: string;
  last_name: string;
  password: string;
  reenter_password: string;
};

export const AcceptInviteCreate = (props: AcceptInviteCreateProps) => {
  const navigate = useNavigate();
  const [acceptInviteSignup, acceptInviteSignupResult] =
    api.useAcceptInviteSignupMutation();

  const inviteDetails = api.useGetInviteDetailsQuery(props.token);

  const { handleSubmit, control, setError, reset } =
    useForm<AcceptInviteCreateForm>({
      defaultValues: {
        first_name: "",
        last_name: "",
        password: "",
        reenter_password: "",
      },
    });

  // Update form when invite details load
  useEffect(() => {
    if (inviteDetails.data) {
      reset({
        first_name: inviteDetails.data.first_name ?? "",
        last_name: inviteDetails.data.last_name ?? "",
        password: "",
        reenter_password: "",
      });
    }
  }, [inviteDetails.data, reset]);

  const loginHref = `/login?${createSearchParams({
    token: props.token,
    ...(inviteDetails.data?.email && { email: inviteDetails.data.email }),
  })}`;

  const onSubmit = handleSubmit(({ first_name, last_name, password }) => {
    acceptInviteSignup({
      first_name,
      last_name,
      password,
      invite_token: props.token,
    }).then((result) => {
      if ("data" in result) {
        navigate("/login");
      }
    });
  });

  useEffect(() => {
    if (acceptInviteSignupResult.isError) {
      const fieldErrors = extractFieldErrors(acceptInviteSignupResult.error);

      // Set field-specific errors
      fieldErrors.forEach(({ field, message }) => {
        if (field in { first_name: 1, last_name: 1, password: 1 }) {
          setError(field as keyof AcceptInviteCreateForm, {
            type: "server",
            message,
          });
        }
      });

      // Show generic error toast if no field errors were found
      if (fieldErrors.length === 0) {
        toast.custom((t) => (
          <IconNotification
            color="error"
            title="Something went wrong."
            description="Please try again later."
            hideDismissLabel
            onClose={() => toast.dismiss(t)}
          />
        ));
      }
    }
  }, [
    acceptInviteSignupResult.isError,
    acceptInviteSignupResult.error,
    setError,
  ]);

  if (inviteDetails.isLoading) {
    return <LoadingIndicator type="brand-spinner" />;
  }

  // If user already exists, show login message instead of signup form
  if (inviteDetails.data?.user_exists) {
    return (
      <div className="flex size-full flex-col justify-center text-center">
        <div className="mb-2xl text-lg font-semibold">
          Account already exists
        </div>
        <div className="mb-3xl text-secondary">
          An account with <strong>{inviteDetails.data.email}</strong> already
          exists. Please log in to accept the invite.
        </div>
        <Button href={loginHref} className="w-full" size="lg">
          Log in
        </Button>
      </div>
    );
  }

  return (
    <Form
      className="flex size-full flex-col justify-center"
      onSubmit={onSubmit}
    >
      <div className="mb-3xl text-lg font-semibold">Join your team</div>
      <Controller
        control={control}
        name="first_name"
        rules={{ required: "First name is required" }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            type="text"
            label="First name"
            placeholder="Enter your first name"
            isRequired
            validationBehavior="aria"
            isInvalid={fieldState.invalid}
            hint={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="last_name"
        rules={{ required: "Last name is required" }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            className="mt-2xl"
            type="text"
            label="Last name"
            placeholder="Enter your last name"
            isRequired
            validationBehavior="aria"
            isInvalid={fieldState.invalid}
            hint={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="password"
        rules={{
          required: "Password is required",
          minLength: {
            value: 8,
            message:
              "This password is too short. It must contain at least 8 characters.",
          },
        }}
        render={({ field, fieldState }) => (
          <>
            <Input
              {...field}
              className="mt-2xl"
              type="password"
              label="Password"
              placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
              isRequired
              validationBehavior="aria"
              isInvalid={fieldState.invalid}
              hint={fieldState.error?.message}
            />
            <PasswordStrength password={field.value} />
          </>
        )}
      />
      <Controller
        control={control}
        name="reenter_password"
        rules={{
          required: "Please reenter your password",
          validate: (reenter_password, { password }) => {
            if (reenter_password !== password) {
              return "Passwords do not match";
            }
          },
        }}
        render={({ field, fieldState }) => (
          <Input
            {...field}
            className="mt-2xl"
            type="password"
            label="Reenter password"
            placeholder="&bull;&bull;&bull;&bull;&bull;&bull;&bull;&bull;"
            isRequired
            validationBehavior="aria"
            isInvalid={fieldState.invalid}
            hint={fieldState.error?.message}
          />
        )}
      />
      <Button
        type="submit"
        className="mt-3xl mb-lg w-full"
        size="lg"
        isLoading={acceptInviteSignupResult.isLoading}
      >
        Create account
      </Button>
      <Button href={loginHref} className="w-full" color="tertiary" size="lg">
        Already have an account? Log in
      </Button>
    </Form>
  );
};
